/* PickListComponent.js */

"use strict";
const BaseMultipleOptionSelectionControlComponent = require("@gluenetworks/input_base_control")
    .OptionSelection.Multiple;
const Renderer = require("./PickListRenderer");
const _ = require("lodash");

class PickListComponent extends BaseMultipleOptionSelectionControlComponent {
    /**
     * @schema.name Pick List Control
     * @schema.package @gluenetworks/picklist
     * @schema.packageIdentifier PickList
     * @schema.events ["ShowFilter", "Options", "Value","DarkTheme", "Focus","Blur", "UserValue", "UserFocus", "UserBlur", "UserKeyDown"]
     *
     * @schema.configuration.optionsLabel.name optionsLabel
     * @schema.configuration.optionsLabel.type string
     * @schema.configuration.optionsLabel.default undefined
     * @schema.configuration.optionsLabel.required false
     *
     * @schema.configuration.selectedLabel.name selectedLabel
     * @schema.configuration.selectedLabel.type string
     * @schema.configuration.selectedLabel.default undefined
     * @schema.configuration.selectedLabel.required false
     *
     * @schema.configuration.hideOptionFilter.name hideOptionFilter
     * @schema.configuration.hideOptionFilter.type boolean
     * @schema.configuration.hideOptionFilter.default false
     * @schema.configuration.hideOptionFilter.required false
     *
     * @schema.configuration.verticalLayout.name verticalLayout
     * @schema.configuration.verticalLayout.type boolean
     * @schema.configuration.verticalLayout.default false
     * @schema.configuration.verticalLayout.required false
     *
     * @schema.configuration.lineCount.name lineCount
     * @schema.configuration.lineCount.type number
     * @schema.configuration.lineCount.default undefined
     * @schema.configuration.lineCount.required false
     *
     * @schema.configuration.moveOnDblClick.name moveOnDblClick
     * @schema.configuration.moveOnDblClick.type boolean
     * @schema.configuration.moveOnDblClick.default true
     * @schema.configuration.moveOnDblClick.required false
     *
     * @schema.configuration.options.example [{"label":"Number 1","value":{"valueProp1":"blah1"}},{"label":"Number 2","value":{"valueProp1":"blah2"}},{"label":"Number 3","value":{"valueProp1":"blah3"}},{"label":"Alpha A","value":{"valueProp1":"blahA"}},{"label":"Alpha B","value":{"valueProp1":"blahB"}},{"label":"Number 4","value":{"valueProp1":"blah4"}},{"label":"Number 5","value":{"valueProp1":"blah5"}},{"label":"Alpha C","value":{"valueProp1":"blahC"}},{"label":"Number 6","value":{"valueProp1":"blah6"}},{"label":"Alpha D","value":{"valueProp1":"blahD"}},{"label":"Alpha E","value":{"valueProp1":"blahE"}},{"label":"A really long value that will overflow the horizontal","value":{"valueProp1":"really long"}}]
     *
     * @schema.configuration.value.example [{ valueProp1: 'blah1', valueProp2: { somethin: 'somethin1' } },{ valueProp1: 'blah2', valueProp2: { somethin: 'somethin2' } }]
     *
     */
    constructor(configOptions) {
        super(configOptions);
        this._optionsLabel = "Options";
        this._selectedLabel = "Selected";
        this._moveOnDblClick = true;
        if (configOptions) {
            if (typeof configOptions.optionsLabel !== "undefined") {
                this._optionsLabel = configOptions.optionsLabel;
            }
            if (typeof configOptions.selectedLabel !== "undefined") {
                this._selectedLabel = configOptions.selectedLabel;
            }

            this._hideOptionFilter = configOptions.hideOptionFilter;
            this._verticalLayout = configOptions.verticalLayout;
            if (typeof configOptions.moveOnDblClick !== "undefined") {
                this._moveOnDblClick = configOptions.moveOnDblClick;
            }
            this.setLineCount(configOptions.lineCount);
        }
        this._selectionHighlight = [];
        this._renderer = new Renderer(this);

        this._registerListeners();
    }
    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS */
    /*******************************************/
    _registerListeners() {}
    _unregisterListeners() {}
    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS */
    /* START PUBLIC COMPONENT MODEL METHODS */
    /*******************************************/
    get renderer() {
        return this.getRenderer();
    }
    getRenderer() {
        return this._renderer;
    }
    shutdown() {
        this._unregisterListeners();
        return this._renderer.shutdown().then(
            () => {
                this._renderer = undefined;
            },
            (/*err*/) => {
                //TODO - Handle any errors in shutting down.
            }
        );
    }

    /*******************************************/
    /* END COMPONENT MODEL METHODS */
    /* START HANDLER METHODS */
    /*******************************************/

    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE METHODS */
    /*******************************************/

    /*******************************************/
    /* END PRIVATE METHODS */
    /* START PUBLIC API METHODS */
    /*******************************************/

    /**
     * @schema.methods.setOptionsLabel.name setOptionsLabel
     * @schema.methods.setOptionsLabel.description Sets the option label text and returns an instance of the control for chainability
     * @schema.methods.setOptionsLabel.returns Instance of the control
     * @schema.methods.setOptionsLabel.parameters.newVal.name newVal
     * @schema.methods.setOptionsLabel.parameters.newVal.description The new option text
     * @schema.methods.setOptionsLabel.parameters.newVal.type string
     */
    setOptionsLabel(newVal) {
        this._optionsLabel = newVal;
        this.emit("OptionsLabel", this._optionsLabel);
        return this;
    }

    /**
     * @schema.methods.getOptionsLabel.name getOptionsLabel
     * @schema.methods.getOptionsLabel.description Returns the option label text
     * @schema.methods.getOptionsLabel.returns string
     */
    getOptionsLabel() {
        return this._optionsLabel;
    }

    get optionsLabel() {
        return this.getOptionsLabel();
    }

    set optionsLabel(newVal) {
        return this.setOptionsLabel(newVal);
    }

    /**
     * @schema.methods.setSelectedLabel.name setSelectedLabel
     * @schema.methods.setSelectedLabel.description Sets the selected label text and returns an instance of the control for chainability
     * @schema.methods.setSelectedLabel.returns Instance of the control
     * @schema.methods.setSelectedLabel.parameters.newVal.name newVal
     * @schema.methods.setSelectedLabel.parameters.newVal.description The new selected text
     * @schema.methods.setSelectedLabel.parameters.newVal.type string
     */
    setSelectedLabel(newVal) {
        this._selectedLabel = newVal;
        this.emit("SelectedLabel", this._selectedLabel);
        return this;
    }

    /**
     * @schema.methods.getSelectedLabel.name getSelectedLabel
     * @schema.methods.getSelectedLabel.description Returns the Selected label text
     * @schema.methods.getSelectedLabel.returns string
     */
    getSelectedLabel() {
        return this._selectedLabel;
    }

    get selectedLabel() {
        return this.getSelectedLabel();
    }

    set selectedLabel(newVal) {
        return this.setSelectedLabel(newVal);
    }

    /**
     * @schema.methods.setVerticalLayout.name setVerticalLayout
     * @schema.methods.setVerticalLayout.description Sets the layout to vertical
     * @schema.methods.setVerticalLayout.returns Instance of the control
     * @schema.methods.setVerticalLayout.parameters.newVal.name newVal
     * @schema.methods.setVerticalLayout.parameters.newVal.description True if vertical layout
     * @schema.methods.setVerticalLayout.parameters.newVal.type boolean
     */
    setVerticalLayout(newVal) {
        this._verticalLayout = newVal;
        this.emit("VerticalLayout", this._verticalLayout);
        return this;
    }

    /**
     * @schema.methods.getVerticalLayout.name getVerticalLayout
     * @schema.methods.getVerticalLayout.description Returns the vertical layout option
     * @schema.methods.getVerticalLayout.returns boolean
     */
    getVerticalLayout() {
        return this._verticalLayout;
    }

    get verticalLayout() {
        return this.getVerticalLayout();
    }

    set verticalLayout(newVal) {
        return this.setVerticalLayout(newVal);
    }

    /**
     * @schema.methods.setHideOptionFilter.name setHideOptionFilter
     * @schema.methods.setHideOptionFilter.description Sets the layout to vertical
     * @schema.methods.setHideOptionFilter.returns Instance of the control
     * @schema.methods.setHideOptionFilter.parameters.newVal.name newVal
     * @schema.methods.setHideOptionFilter.parameters.newVal.description True if vertical layout
     * @schema.methods.setHideOptionFilter.parameters.newVal.type boolean
     */
    setHideOptionFilter(newVal) {
        this._hideOptionFilter = newVal;
        this.emit("HideOptionFilter", this._hideOptionFilter);
        return this;
    }

    /**
     * @schema.methods.getHideOptionFilter.name getHideOptionFilter
     * @schema.methods.getHideOptionFilter.description Returns the vertical layout option
     * @schema.methods.getHideOptionFilter.returns boolean
     */
    getHideOptionFilter() {
        return this._hideOptionFilter;
    }

    get hideOptionFilter() {
        return this.getHideOptionFilter();
    }

    set hideOptionFilter(newVal) {
        return this.setHideOptionFilter(newVal);
    }

    /**
     * @schema.methods.setLineCount.name setLineCount
     * @schema.methods.setLineCount.description Sets the line count on the list, must be greater or equal to 4
     * @schema.methods.setLineCount.returns Instance of the control
     * @schema.methods.setLineCount.parameters.newVal.name newVal
     * @schema.methods.setLineCount.parameters.newVal.description The new value
     * @schema.methods.setLineCount.parameters.newVal.type number
     */
    setLineCount(newVal) {
        if (newVal >= 4) {
            this._lineCount = newVal;
            this.emit("LineCount", this._lineCount);
        }
        return this;
    }

    /**
     * @schema.methods.getLineCount.name getLineCount
     * @schema.methods.getLineCount.description Returns the vertical layout option
     * @schema.methods.getLineCount.returns boolean
     */
    getLineCount() {
        return this._lineCount;
    }

    get lineCount() {
        return this.getLineCount();
    }

    set lineCount(newVal) {
        return this.setLineCount(newVal);
    }

    /**
     * @schema.methods.setSelectionHighlight.name setSelectionHighlight
     * @schema.methods.setSelectionHighlight.description Sets the selected items in the selection list
     * @schema.methods.setSelectionHighlight.returns Instance of the control
     * @schema.methods.setSelectionHighlight.parameters.newVal.name newVal
     * @schema.methods.setSelectionHighlight.parameters.newVal.description The new value
     * @schema.methods.setSelectionHighlight.parameters.newVal.type number
     */
    setSelectionHighlight(newVal) {
        if (!_.isEqual(newVal, this._selectionHighlight)) {
            this._selectionHighlight = newVal;
            this.emit("SelectionClick", this._selectionHighlight);
        }
        return this;
    }

    /**
     * @schema.methods.getSelectionHighlight.name getSelectionHighlight
     * @schema.methods.getSelectionHighlight.description Returns the vertical layout option
     * @schema.methods.getSelectionHighlight.returns boolean
     */
    getSelectionHighlight() {
        return this._selectionHighlight;
    }

    get selectionHighlight() {
        return this.getSelectionHighlight();
    }

    set selectionHighlight(newVal) {
        return this.setSelectionHighlight(newVal);
    }

    /**
     * @schema.methods.setMoveOnDblClick.name setMoveOnDblClick
     * @schema.methods.setMoveOnDblClick.description Sets the layout to vertical
     * @schema.methods.setMoveOnDblClick.returns Instance of the control
     * @schema.methods.setMoveOnDblClick.parameters.newVal.name newVal
     * @schema.methods.setMoveOnDblClick.parameters.newVal.description True if vertical layout
     * @schema.methods.setMoveOnDblClick.parameters.newVal.type boolean
     */
    setMoveOnDblClick(newVal) {
        this._moveOnDblClick = newVal;
        this.emit("MoveOnDblClick", this._moveOnDblClick);
        return this;
    }

    /**
     * @schema.methods.getMoveOnDblClick.name getMoveOnDblClick
     * @schema.methods.getMoveOnDblClick.description Returns the vertical layout option
     * @schema.methods.getMoveOnDblClick.returns boolean
     */
    getMoveOnDblClick() {
        return this._moveOnDblClick;
    }

    get moveOnDblClick() {
        return this.getMoveOnDblClick();
    }

    set moveOnDblClick(newVal) {
        return this.setMoveOnDblClick(newVal);
    }

    /*******************************************/
    /* END PUBLIC API METHODS */
    /*******************************************/
}
module.exports = PickListComponent;
